import React, { useState } from 'react';
import { Link } from '@inertiajs/react';
import { marked } from 'marked';
import Layout from '../../../components/Layout.jsx';

marked.setOptions({ gfm: true, breaks: false });

const STATUS_BADGE = {
  draft:     'bg-gray-100 text-gray-700',
  published: 'bg-emerald-100 text-emerald-700',
  archived:  'bg-amber-100 text-amber-700',
};

function formatPrice(price) {
  if (price == null) return null;
  if (Number(price) === 0) return 'Included';
  return `$${Number(price).toFixed(2)}`;
}

function formatDuration(minutes) {
  if (!minutes) return null;
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  const rem = minutes % 60;
  return rem ? `${hours}h ${rem}m` : `${hours}h`;
}

function formatTimestamp(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleString(undefined, { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });
}

function Section({ title, children }) {
  return (
    <section className="bg-white border border-gray-200 rounded-xl p-6">
      <h2 className="text-base font-semibold text-gray-900">{title}</h2>
      <div className="mt-3 text-sm text-gray-700 leading-relaxed">{children}</div>
    </section>
  );
}

function Meta({ label, value, mono }) {
  if (value == null || value === '') return null;
  return (
    <div>
      <dt className="text-xs uppercase tracking-wide text-gray-500">{label}</dt>
      <dd className={`mt-0.5 text-sm text-gray-900 ${mono ? 'font-mono text-xs' : ''}`}>{value}</dd>
    </div>
  );
}

const UNIT_TYPE_LABEL = {
  pbEbook: 'Ebook',
  pdf: 'PDF',
  ivideo: 'Video',
  youtube: 'YouTube',
  audio: 'Audio',
  assessmentV2: 'Quiz',
  newSurvey: 'Survey',
  certificateCompletion_v2: 'Certificate',
  teamsMeeting: 'Teams meeting',
};

function EbookContent({ content }) {
  const html = React.useMemo(() => marked.parse(content.body || ''), [content.body]);
  return (
    <div className="px-4 py-4 bg-gray-50 border-t border-gray-100 overflow-x-auto">
      <div
        className="ebook-content text-sm text-gray-800 leading-relaxed break-words [&_h1]:text-xl [&_h1]:font-semibold [&_h1]:text-gray-900 [&_h1]:mt-4 [&_h1]:mb-2 [&_h2]:text-lg [&_h2]:font-semibold [&_h2]:text-gray-900 [&_h2]:mt-4 [&_h2]:mb-2 [&_h3]:text-base [&_h3]:font-semibold [&_h3]:text-gray-900 [&_h3]:mt-3 [&_h3]:mb-1.5 [&_p]:mb-3 [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:mb-3 [&_ol]:list-decimal [&_ol]:pl-5 [&_ol]:mb-3 [&_li]:mb-1 [&_strong]:font-semibold [&_em]:italic [&_blockquote]:border-l-4 [&_blockquote]:border-gray-300 [&_blockquote]:pl-3 [&_blockquote]:italic [&_blockquote]:text-gray-600 [&_blockquote]:my-3 [&_code]:bg-gray-100 [&_code]:px-1 [&_code]:rounded [&_code]:text-xs [&_table]:w-full [&_table]:my-3 [&_table]:border [&_table]:border-gray-300 [&_th]:bg-gray-100 [&_th]:p-1.5 [&_th]:text-left [&_th]:font-semibold [&_th]:border [&_th]:border-gray-300 [&_td]:p-1.5 [&_td]:border [&_td]:border-gray-300"
        dangerouslySetInnerHTML={{ __html: html }}
      />
    </div>
  );
}

function QuizContent({ content }) {
  return (
    <div className="px-4 py-4 bg-gray-50 border-t border-gray-100 space-y-4">
      <div className="text-xs text-gray-500">
        Passing score: <span className="font-semibold text-gray-700">{content.passing_score}%</span>
        {' · '}
        {content.questions.length} question{content.questions.length === 1 ? '' : 's'}
      </div>
      <ol className="space-y-4 list-decimal list-inside">
        {content.questions.map((q) => (
          <li key={q.id} className="text-sm text-gray-900">
            <span className="font-medium">{q.prompt}</span>
            <ul className="mt-2 ml-4 space-y-1">
              {q.options.map((opt) => (
                <li
                  key={opt.id}
                  className={`flex items-start gap-2 text-sm ${
                    opt.is_correct ? 'text-emerald-800 font-medium' : 'text-gray-700'
                  }`}
                >
                  <span className={`mt-1 w-2 h-2 rounded-full flex-shrink-0 ${opt.is_correct ? 'bg-emerald-500' : 'bg-gray-300'}`} />
                  <span>{opt.label}</span>
                  {opt.is_correct && (
                    <span className="ml-1 text-[10px] uppercase tracking-wide text-emerald-700 bg-emerald-100 px-1.5 py-0.5 rounded">correct</span>
                  )}
                </li>
              ))}
            </ul>
            {q.explanation && (
              <p className="mt-2 ml-4 text-xs text-gray-500 italic">{q.explanation}</p>
            )}
          </li>
        ))}
      </ol>
    </div>
  );
}

function UnitRow({ unit }) {
  const [open, setOpen] = useState(false);
  const label = UNIT_TYPE_LABEL[unit.unit_type] || unit.unit_type || 'Unit';
  const toggleLabel = unit.content?.kind === 'ebook' ? 'ebook' : 'quiz';
  return (
    <li className={unit.removed ? 'opacity-60' : ''}>
      <div className="px-4 py-2 flex items-center justify-between gap-3 min-w-0">
        <div className="flex-1 min-w-0">
          <p className="text-sm text-gray-900 truncate">
            {unit.title}
            {unit.removed && (
              <span className="ml-2 text-xs font-medium text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded">
                removed
              </span>
            )}
          </p>
          {unit.subtitle && (
            <p className="text-xs text-gray-500 truncate">{unit.subtitle}</p>
          )}
        </div>
        <div className="flex items-center gap-3 flex-shrink-0">
          {unit.content && (
            <button
              type="button"
              onClick={() => setOpen((o) => !o)}
              className="text-xs font-medium text-indigo-600 hover:text-indigo-800"
            >
              {open ? `Hide ${toggleLabel}` : `View ${toggleLabel}`}
            </button>
          )}
          <span className="text-xs font-medium text-indigo-700 bg-indigo-50 px-2 py-0.5 rounded">
            {label}
          </span>
        </div>
      </div>
      {open && unit.content && (
        unit.content.kind === 'ebook'
          ? <EbookContent content={unit.content} />
          : <QuizContent content={unit.content} />
      )}
    </li>
  );
}

function SectionsPanel({ sections = [] }) {
  const activeCount = sections.filter((s) => !s.removed).length;
  const removedCount = sections.length - activeCount;
  if (sections.length === 0) {
    return (
      <Section title="Course structure">
        <p className="text-gray-500">
          No sections imported yet. Run{' '}
          <code className="px-1 py-0.5 rounded bg-gray-100 text-gray-800 text-xs font-mono">
            bin/rails learn_worlds:import_course_contents
          </code>{' '}
          to pull the section + learning-unit structure from LearnWorlds.
        </p>
      </Section>
    );
  }

  return (
    <Section title={`Course structure (${activeCount} sections${removedCount ? `, ${removedCount} removed` : ''})`}>
      <div className="space-y-4">
        {sections.map((section) => (
          <div
            key={section.id}
            className={`rounded-lg border ${section.removed ? 'border-gray-200 bg-gray-50 opacity-60' : 'border-gray-200'}`}
          >
            <div className="px-4 py-3 border-b border-gray-100 flex items-start justify-between gap-3">
              <div>
                <h3 className="text-sm font-semibold text-gray-900">
                  {section.title}
                  {section.removed && (
                    <span className="ml-2 text-xs font-medium text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded">
                      removed from LW
                    </span>
                  )}
                </h3>
                {section.description && (
                  <p className="mt-1 text-xs text-gray-600">{section.description}</p>
                )}
              </div>
              {section.access && (
                <span className="text-xs text-gray-500 uppercase tracking-wide">
                  {section.access}
                </span>
              )}
            </div>
            {section.units.length === 0 ? (
              <div className="px-4 py-3 text-xs text-gray-400">No learning units.</div>
            ) : (
              <ul className="divide-y divide-gray-100">
                {section.units.map((unit) => <UnitRow key={unit.id} unit={unit} />)}
              </ul>
            )}
          </div>
        ))}
      </div>
    </Section>
  );
}

export default function SuperadminCoursesShow({ course, sections = [] }) {
  const price = formatPrice(course.price);
  const duration = formatDuration(course.duration_minutes);
  const badgeClass = STATUS_BADGE[course.status] || 'bg-gray-100 text-gray-700';

  return (
    <Layout>
      <div className="max-w-5xl mx-auto p-6 space-y-6">
        <div>
          <Link href="/superadmin/courses" className="text-sm text-indigo-600 hover:text-indigo-800">
            ← Back to courses
          </Link>
        </div>

        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
          {course.image_url && (
            <div className="aspect-[3/1] bg-gray-100 overflow-hidden">
              <img src={course.image_url} alt="" className="w-full h-full object-cover" />
            </div>
          )}
          <div className="p-6">
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <h1 className="text-2xl font-semibold text-gray-900">{course.title}</h1>
              <span className={`text-xs font-medium px-2.5 py-1 rounded-full ${badgeClass}`}>
                {course.status}
              </span>
            </div>
            {course.summary && (
              <p className="mt-2 text-gray-600">{course.summary}</p>
            )}
            <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-gray-600">
              {course.ceu_credit_hours != null && (
                <span>
                  <span className="font-semibold text-gray-900">{course.ceu_credit_hours}</span> CEU
                </span>
              )}
              {duration && <span>{duration}</span>}
              {price && (
                <span className="ml-auto text-lg font-semibold text-gray-900">{price}</span>
              )}
            </div>
          </div>
        </div>

        <Section title="Sync metadata">
          <dl className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Meta label="LearnWorlds ID" value={course.learn_worlds_id || '—'} mono />
            <Meta label="Slug" value={course.slug} mono />
            <Meta label="Last synced" value={formatTimestamp(course.synced_at) || '—'} />
            <Meta label="Published at" value={formatTimestamp(course.published_at) || '—'} />
          </dl>
        </Section>

        <SectionsPanel sections={sections} />

        {course.description && (
          <Section title="About this course">
            <div className="whitespace-pre-wrap">{course.description}</div>
          </Section>
        )}

        {course.learning_objectives && (
          <Section title="Learning objectives">
            <div className="whitespace-pre-wrap">{course.learning_objectives}</div>
          </Section>
        )}

        {course.course_toc && (
          <Section title="Course outline">
            <div className="whitespace-pre-wrap">{course.course_toc}</div>
          </Section>
        )}
      </div>
    </Layout>
  );
}
